import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RandomGenerator from './components/RandomGenerator';
import LuckyGenerator from './components/LuckyGenerator';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { supabase } from '@/lib/supabase';
import SmartRecommender from './components/SmartRecommender';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import AdSlot from '@/components/feature/AdSlot';
import { usePageMeta } from '@/hooks/usePageMeta';
import Reveal from '@/components/base/Reveal';

export default function Home() {
  const [latestDraw, setLatestDraw] = useState<number>(1152);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLatest = async () => {
      const { data } = await supabase
        .from('draw_history')
        .select('id')
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        setLatestDraw(data.id);
      }
    };
    fetchLatest();
  }, []);

  usePageMeta({
    title: 'LottoLab | 무료 로또 번호 생성·통계 분석·스마트 추천',
    description:
      'LottoLab은 무료 로또 번호 생성 서비스입니다. 랜덤 생성기·행운 생성기로 나만의 번호를 만들고, 역대 당첨번호 통계 분석과 데이터 기반 스마트 추천으로 더 똑똑하게 로또 번호를 선택하세요. 번호 저장과 당첨 확인까지 한 번에!',
    keywords:
      '로또 번호 생성, 로또 번호 추천, 로또 통계 분석, 무료 로또, 로또 번호 랜덤 생성, LottoLab, 로또랩',
    canonical: 'https://lottolab.app',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero area */}
      <section className="relative pt-16">
        <div className="w-full px-4 md:px-6 py-16 md:py-24 text-center">
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium mb-6">
                <i className="ri-flask-line"></i>
                <span>데이터로 실험하는 나만의 번호 연구소</span>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <h1 className="font-heading text-4xl md:text-6xl text-foreground-900 mb-4 leading-tight font-bold">
                행운을 <span className="text-secondary-600">실험하다</span>,
                <br />
                <span className="text-primary-600">Lotto</span><span className="text-accent-600">Lab</span>
              </h1>
            </Reveal>

            <Reveal delay={200}>
              <p className="text-foreground-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
                랜덤 생성부터 스마트 추천, 통계 분석까지.
                <br className="hidden sm:block" />
                데이터 기반 번호 추천으로 로또의 새로운 재미를 느껴보세요.
              </p>
            </Reveal>

            {/* Quick stats — 유리 morphism 카드 */}
            <Reveal delay={300}>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-10">
              <div className="text-center bg-background-100 rounded-2xl px-6 py-4 hover:bg-background-200 transition-colors duration-300 cursor-default">
                <p className="font-heading text-3xl md:text-4xl text-accent-600">{latestDraw.toLocaleString()}</p>
                <p className="text-foreground-500 text-xs mt-1">누적 회차</p>
              </div>
              <div className="text-center bg-background-100 rounded-2xl px-6 py-4 hover:bg-background-200 transition-colors duration-300 cursor-default">
                <p className="font-heading text-3xl md:text-4xl text-primary-500">814만</p>
                <p className="text-foreground-500 text-xs mt-1">조합의 수</p>
              </div>
              <div className="text-center bg-background-100 rounded-2xl px-6 py-4 hover:bg-background-200 transition-colors duration-300 cursor-default">
                <p className="font-heading text-3xl md:text-4xl text-secondary-600">6/45</p>
                <p className="text-foreground-500 text-xs mt-1">당첨 확률</p>
              </div>
            </div>
            </Reveal>

            {/* Guide CTA for new visitors */}
            <Reveal delay={400}>
              <div className="mt-8">
                <button
                  onClick={() => navigate('/guide')}
                  className="
                    inline-flex items-center gap-2 px-6 py-3 rounded-full
                    bg-primary-500 text-white text-sm font-medium
                    hover:bg-primary-600
                    transition-colors duration-200 whitespace-nowrap cursor-pointer
                  "
                >
                  <i className="ri-book-open-line"></i>
                  처음 오셨나요? 이용방법 보러가기
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Random Generator */}
      <RandomGenerator />

      {/* Lucky Generator */}
      <LuckyGenerator />

      {/* Ad Slot 1 — 홈 중간 배너 */}
      <section className="w-full px-4 md:px-6 py-6">
        <Reveal className="max-w-6xl mx-auto">
          <AdSlot type="kakao" width={728} height={90} />
        </Reveal>
      </section>

      {/* Smart Recommender */}
      <SmartRecommender />

      {/* Ad Slot 2 — 하단 배너 */}
      <section className="w-full px-4 md:px-6 py-6">
        <Reveal className="max-w-6xl mx-auto">
          <AdSlot type="google" width={728} height={90} />
        </Reveal>
      </section>

      {/* CTA section */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20">
        <Reveal className="max-w-3xl mx-auto">
          <div className="bg-primary-800 rounded-3xl p-10 md:p-16 text-center text-white">
          <h2 className="font-heading text-2xl md:text-4xl mb-4 font-bold">
            매주 새로운 행운이 기다리고 있어요
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8 max-w-lg mx-auto">
            통계 분석과 스마트 추천으로 더 똑똑하게 번호를 골라보세요.
            <br />
            다음 주 행운의 주인공은 바로 당신!
          </p>
          <a
            href="/statistics"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE('/statistics');
            }}
            className="
              inline-flex items-center gap-2 px-8 py-3.5 rounded-full
              bg-secondary-600 text-white font-bold text-base
              hover:bg-secondary-700 transition-all duration-200
              whitespace-nowrap cursor-pointer
            "
          >
            <i className="ri-bar-chart-line"></i>
            통계 분석 보러가기
          </a>
          </div>
        </Reveal>
      </section>

      <Footer />
      <div className="md:hidden h-16"></div>
      <MobileBottomNav />
    </div>
  );
}